import { TrustBlock } from "./Trust.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, money, day, dateTime } from "../../api.js";
import { useResource } from "../useResource.js";
import { shortName } from "../common.jsx";
import { InvoicePanel } from "../Erpn.jsx";
import { Pager } from "../common.jsx";
import { DownloadButton } from "../Reports.jsx";
import AccountsTab from "./Accounts.jsx";
import { documentAccount, ibanGroups } from "../../../../lib/counterparties/accounts.js";

// Досьє контрагента (docs/counterparty-dossier-improvement-plan.md, D01/D03; docs/research/2026-10-12-counterparty-dossier-ux.md):
// постійна шапка з кодами, станом і діями; огляд для швидкого рішення; тематичні вкладки з повними даними. Джерело й
// дата — біля фактів; прогалини перевірки видно вже в огляді й не плутаються з ризиками. «Усі відомості» і друк
// показують усі вкладки підряд. Вкладка — у адресі (?tab=…), тож «Назад» і пряме посилання її відновлюють.

const RANK = { preparer: 1, submitter: 2, owner: 3 };
const canEdit = (company) => (RANK[company?.role] || 0) >= 1;
const uah = (v) => `${money(v)} грн`;
const isoUa = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join(".") : "—");
const TABS = [["overview", "Огляд"], ["company", "Компанія"], ["accounts", "Рахунки"], ["taxes", "Податки"], ["finance", "Фінанси"], ["checks", "Перевірки"], ["deals", "Співпраця"], ["sources", "Джерела"]];
const SRC_STATE = { ready: ["підключено", "ok"], not_connected: ["не підключено", "idle"], consent_required: ["лише за згодою контрагента", "idle"] };
const YEAR_STATE = { available: "є", not_found: "немає в наборі", not_imported: "не імпортовано", source_unavailable: "не отримано",
  conflict: "конфлікт версій", before_registration: "до реєстрації" };

const scoreTone = (n) => (n >= 7 ? "ok" : n >= 5 ? "warn" : "bad");
const fmtEffect = (e) => `${e > 0 ? "+" : "−"}${String(Math.abs(e)).replace(".", ",")}`;

// Оцінка надійності 1–10: число, рівень, чинники «+/−» з джерелами, неперевірені джерела (оцінку не знижують)
function ScoreCard({ score }) {
  return (
    <section className="card score-card" aria-labelledby="ov-score">
      <div className="score-head">
        <div className={`score-num score-${scoreTone(score.score)}`} aria-hidden="true">{score.score}<span>/10</span></div>
        <div>
          <h3 id="ov-score" className="block-title">Надійність: {score.score} з 10 — {score.level}</h3>
          <p className="muted small">Старт — {score.base} («негативних фактів не виявлено»); змінюють лише підтверджені факти.
            Оцінка за {score.coverage.checked} з {score.coverage.total} джерел{score.coverage.preliminary ? " — попередня" : ""}.</p>
        </div>
      </div>
      {score.caps.length > 0 && <ul className="plain-list">{score.caps.map((c) => <li key={c.key} className="error">{c.text} — оцінка не вище {c.max} <span className="muted small">· {c.source}</span></li>)}</ul>}
      {score.factors.length > 0 ? (
        <ul className="score-factors">{score.factors.map((f) => (
          <li key={f.key}><b className={f.effect > 0 ? "score-plus" : "score-minus"}>{fmtEffect(f.effect)}</b> {f.text} <span className="muted small">· {f.source}</span></li>
        ))}</ul>
      ) : <p className="muted">Підтверджених фактів, що змінюють оцінку, немає.</p>}
      {score.coverage.missing.length > 0 && <p className="notice">Не перевірено: {score.coverage.missing.join(", ")}. Відсутність даних оцінку не знижує.</p>}
      <p className="muted small">Власна пояснювана оцінка eCabinet (правила {score.rules}), не офіційний статус ДПС чи висновок про порушення.</p>
    </section>
  );
}

// Короткий стан ПДВ для шапки й огляду
function vatShort(vat) {
  if (!vat || vat.state === "not_checked") return { text: "ПДВ не перевірено", tone: "idle" };
  if (vat.state === "registered") return { text: `Платник ПДВ з ${isoUa(vat.registrationDate)}`, tone: "ok" };
  if (vat.state === "annulled") return { text: `ПДВ анульовано ${isoUa(vat.annulmentDate)}`, tone: "bad" };
  if (vat.state === "conflict") return { text: "Реєстр ПДВ: кілька чинних ІПН", tone: "warn" };
  return { text: "Не платник ПДВ (за реєстром)", tone: "idle" };
}

function Copy({ text, label }) {
  const [done, setDone] = useState(false);
  const go = async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* немає доступу до буфера */ } };
  return <button type="button" className="copy-btn no-print" onClick={go} aria-label={`Копіювати ${label}`}>{done ? "скопійовано" : "⧉"}</button>;
}

export default function PartnerDossier({ companyId, company, tin: subPath, versions }) {
  const [tin, query] = subPath.split("?");
  const params = new URLSearchParams(query || "");
  const autoCheck = params.get("check") === "1";
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "overview";
  const [reload, setReload] = useState(0);
  const r = useResource(`/api/companies/${companyId}/partners/${tin}`, `${versions.erpn}:${reload}`);
  const dz = useResource(`/api/companies/${companyId}/partners/${tin}/dossier`, `${versions.erpn}:${reload}`);
  const [checking, setChecking] = useState(false), [checkError, setCheckError] = useState(null);
  const [listBusy, setListBusy] = useState(false), [listError, setListError] = useState(null);
  const [all, setAll] = useState(false), [printing, setPrinting] = useState(false);
  const [dealFilter, setDealFilter] = useState("all");
  const [msg, setMsg] = useState(null);
  const [accEdited, setAccounts] = useState(null); // рахунки після змін у вкладці — до наступного завантаження досьє
  useEffect(() => setAccounts(null), [dz.data]);
  const tabRefs = useRef({});

  const go = (t, extra = {}) => {
    if (extra.filter) setDealFilter(extra.filter);
    location.hash = `#/c/${companyId}/partners/${tin}${t === "overview" ? "" : `?tab=${t}`}`;
  };
  const check = async () => {
    setChecking(true); setCheckError(null);
    try { await api(`/api/companies/${companyId}/counterparties/${tin}/check`, { method: "POST" }); setReload((x) => x + 1); }
    catch (e) { setCheckError(e.message); }
    finally { setChecking(false); }
  };
  const toggleList = async () => {
    setListBusy(true); setListError(null);
    try {
      if (dz.data?.inList) await api(`/api/companies/${companyId}/partners/${tin}/list`, { method: "DELETE" });
      else await api(`/api/companies/${companyId}/partners`, { method: "POST", body: { tin } });
      setReload((x) => x + 1);
    } catch (e) { setListError(e.message); }
    finally { setListBusy(false); }
  };
  // Посилання «перевірити код» (?check=1): одна перевірка, потім адреса без параметра
  useEffect(() => {
    if (!autoCheck) return;
    history.replaceState(null, "", `#/c/${companyId}/partners/${tin}`);
    check();
  }, [tin]); // eslint-disable-line react-hooks/exhaustive-deps
  // Друк: усі вкладки підряд, потім повернення до попереднього вигляду
  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    addEventListener("afterprint", done, { once: true });
    const t = setTimeout(() => window.print(), 50);
    return () => { clearTimeout(t); removeEventListener("afterprint", done); };
  }, [printing]);

  const d = r.data, dos = dz.data;
  const verdict = dos?.tax?.verdict || d?.check?.verdict;
  const reg = dos?.registry;
  const edr = reg?.state === "present" ? reg : null;
  const dpsReg = verdict?.registration || null;
  const inv = d?.invoices || [];
  const name = shortName(edr?.short_name || d?.name || dos?.name) || `Код ${tin}`;
  const vs = vatShort(dos?.vat);
  const director = edr?.signers?.[0] || null;
  const accounts = accEdited || dos?.accounts || [];
  const mainAcc = documentAccount(accounts);

  const requisites = [edr?.name || dos?.name || d?.name, `${tin.length === 8 ? "ЄДРПОУ" : "РНОКПП"} ${tin}`,
    dos?.vat?.state === "registered" && dos.vat.vatNumber ? `ІПН ${dos.vat.vatNumber}` : null,
    dpsReg?.address ? `Адреса: ${dpsReg.address}` : null,
    mainAcc ? `IBAN: ${mainAcc.iban}${mainAcc.bank ? `, ${mainAcc.bank}` : ""}` : null, director ? `Керівник: ${director.split(";")[0]}` : null].filter(Boolean).join("\n");
  const copyReq = async () => {
    try { await navigator.clipboard.writeText(requisites); setMsg("Реквізити скопійовано"); } catch { setMsg("Браузер не дав доступу до буфера обміну"); }
    setTimeout(() => setMsg(null), 2500);
  };

  const onTabKey = (e, i) => {
    const n = e.key === "ArrowRight" ? (i + 1) % TABS.length : e.key === "ArrowLeft" ? (i - 1 + TABS.length) % TABS.length
      : e.key === "Home" ? 0 : e.key === "End" ? TABS.length - 1 : null;
    if (n == null) return;
    e.preventDefault();
    go(TABS[n][0]);
    setTimeout(() => tabRefs.current[TABS[n][0]]?.focus(), 0);
  };

  const ctx = { refresh: () => setReload((x) => x + 1), companyId, company, name, tin, d, dos, verdict, edr, reg, dpsReg, inv, vs, director, mainAcc, accounts, go, check, checking, checkError, dealFilter, setDealFilter, r };
  const panels = {
    overview: <Overview {...ctx} />, company: <CompanyTab {...ctx} />,
    accounts: <AccountsTab companyId={companyId} tin={tin} accounts={accounts} canEdit={canEdit(company)} onChange={setAccounts} />, taxes: <TaxesTab {...ctx} />, finance: <FinanceTab {...ctx} />,
    checks: <ChecksTab {...ctx} />, deals: <DealsTab {...ctx} />, sources: <SourcesTab {...ctx} />,
  };
  const showAll = all || printing;
  const dates = [edr && `ЄДР на ${isoUa(edr.asOf)}`, dos?.tax?.state === "checked" ? `ДПС ${dateTime(dos.tax.checkedAt)}` : "ДПС не перевіряли"].filter(Boolean);

  return (
    <>
      <p className="crumbs no-print"><a href={`#/c/${companyId}/partners`}>← Контрагенти</a></p>
      <header className="dz-head">
        <div className="dz-title">
          <h2>{name}</h2>
          <p className="dz-codes">
            <span>{tin.length === 8 ? "ЄДРПОУ" : "РНОКПП"} <b className="mono">{tin}</b><Copy text={tin} label="код" /></span>
            {dos?.vat?.vatNumber && <span>ІПН <b className="mono">{dos.vat.vatNumber}</b><Copy text={dos.vat.vatNumber} label="ІПН" />{dos.vat.state === "annulled" && <span className="error"> (анульований)</span>}</span>}
          </p>
          <p className="dz-status">
            {dos?.score && <button type="button" className={`badge score-badge badge-${scoreTone(dos.score.score)}`} onClick={() => go("overview")} title="Чому така оцінка — в «Огляді»">
              Надійність {dos.score.score}/10{dos.score.coverage.preliminary ? " · попередня" : ""}</button>}
            {edr && <span className={`badge badge-${edr.stan === "зареєстровано" ? "ok" : "bad"}`}>{edr.stan}</span>}
            {reg?.state === "not_found" && <span className="badge badge-warn">немає у відкритому ЄДР</span>}
            <span className={`badge badge-${vs.tone}`}>{vs.text}</span>
            {dos?.inList && <span className="badge badge-idle" title={dos.inList.note || ""}>у списку компанії</span>}
          </p>
          <p className="muted small">Дані: {dates.join(" · ")}</p>
        </div>
        <div className="dz-actions no-print">
          <button type="button" className="btn btn-sm" onClick={check} disabled={checking}>{checking ? "Перевіряємо ДПС…" : "Оновити ДПС"}</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={copyReq}>Скопіювати реквізити</button>
          <DownloadButton path={`/api/companies/${companyId}/partners/${tin}/export.xlsx`}>Excel</DownloadButton>
          <DownloadButton path={`/api/companies/${companyId}/partners/${tin}/export.pdf`}>PDF</DownloadButton>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPrinting(true)}>Друк</button>
          {canEdit(company) && dos && <button type="button" className="btn btn-sm btn-ghost" onClick={toggleList} disabled={listBusy}>{dos.inList ? "Прибрати зі списку" : "Додати до списку"}</button>}
        </div>
      </header>
      {(msg || listError || checkError) && <p className={listError || checkError ? "error" : "muted small"} role={listError || checkError ? "alert" : "status"}>{listError || checkError || msg}</p>}

      <div className="dz-nav no-print">
        <div className="tabs dz-tabs" role="tablist" aria-label="Розділи досьє">
          {TABS.map(([k, label], i) => (
            <button key={k} type="button" role="tab" id={`dz-tab-${k}`} aria-controls={`dz-panel-${k}`} aria-selected={!showAll && tab === k}
              tabIndex={tab === k ? 0 : -1} ref={(el) => { tabRefs.current[k] = el; }} className={!showAll && tab === k ? "tab active" : "tab"}
              onClick={() => { setAll(false); go(k); }} onKeyDown={(e) => onTabKey(e, i)}>
              {label}{k === "deals" && inv.length ? ` (${inv.length})` : ""}{k === "accounts" && accounts.length ? ` (${accounts.length})` : ""}
            </button>
          ))}
        </div>
        <label className="dz-all"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Усі відомості</label>
      </div>

      {dz.pending && !dos ? <p className="muted" role="status">Завантаження…</p>
        : dz.error && !dos ? <p className="error">Не вдалося завантажити досьє: {dz.error.message}</p>
        : showAll ? TABS.map(([k, label]) => (
          <section key={k} className="dz-section" aria-labelledby={`dz-all-${k}`}>
            <h3 id={`dz-all-${k}`} className="dz-section-title">{label}</h3>
            {panels[k]}
          </section>
        ))
        : <div role="tabpanel" id={`dz-panel-${tab}`} aria-labelledby={`dz-tab-${tab}`}>{panels[tab]}</div>}
      <p className="print-only muted small">Сформовано eCabinet {dateTime(new Date().toISOString())}. Не є офіційним витягом з ЄДР чи довідкою ДПС.</p>
    </>
  );
}

// ---------------------------------------------------------------- Огляд
function Overview({ refresh, companyId, company, name, tin, dos, verdict, edr, reg, dpsReg, inv, vs, director, mainAcc, accounts, go, d }) {
  const attention = [];
  if (edr && edr.stan !== "зареєстровано") attention.push(["bad", `ЄДР: ${edr.stan}`, "company"]);
  if (edr && Object.keys(edr.termination || {}).length) attention.push(["bad", `Припинення: ${Object.values(edr.termination).join(" · ")}`, "company"]);
  if (dos.vat?.state === "annulled") attention.push(["bad", vs.text, "taxes"]);
  if (dos.vat?.state === "conflict") attention.push(["warn", vs.text, "taxes"]);
  for (const it of verdict?.items || []) if (it.tone === "bad" && it.key !== "pdv") attention.push(["bad", it.title, "taxes"]);
  if (verdict?.unchecked?.length) attention.push(["warn", `ДПС не відповіла: ${verdict.unchecked.join(", ")}`, "taxes"]);
  if (dos.tax?.stale) attention.push(["warn", "Відомості ДПС старші за 30 днів — оновіть", "taxes"]);
  const ck = dos.checks || {};
  if (ck.sanctions?.items?.some((x) => x.status === "active")) attention.push(["bad", "Санкції РНБО: діючі", "checks"]);
  if (ck.debtors?.items?.length) attention.push(["bad", `Єдиний реєстр боржників: ${ck.debtors.items.length} запис(ів)`, "checks"]);
  const asDebtor = (ck.enforcement?.active || []).filter((x) => x.role === "debtor").length;
  if (asDebtor) attention.push(["warn", `Незавершені виконавчі провадження як боржник: ${asDebtor}`, "checks"]);
  const defend = (ck.courts?.cases || []).filter((x) => x.roles.some((r) => /відповідач|боржник/i.test(r))).length;
  if (defend) attention.push(["warn", `Судові справи як відповідач (збіг за назвою): ${defend}`, "checks"]);
  // СМКОР за згодою партнера (бізнес-партнерство) — офіційний статус ДПС
  const dd = (x) => String(x || "").slice(0, 10).split("-").reverse().join(".");
  if (dos.smkor?.risky) attention.unshift(["bad", `СМКОР: ризиковий платник${dos.smkor.since ? ` з ${dd(dos.smkor.since)}` : ""}${dos.smkor.kind ? ` (${dos.smkor.kind})` : ""} — дані ДПС`, "overview"]);
  else if (dos.smkor?.was) attention.push(["warn", `СМКОР: був у переліку ризикових${dos.smkor.until ? ` до ${dd(dos.smkor.until)}` : ""}`, "overview"]);
  const gaps = [];
  if (dos.tax?.state !== "checked") gaps.push(["Реєстри ДПС не перевіряли", "taxes"]);
  if (reg?.state === "not_connected") gaps.push(["ЄДР ще не завантажено", "sources"]);
  if (reg?.state === "not_found") gaps.push(["У відкритому ЄДР запису немає", "company"]);
  if (dos.financials?.state === "source_unavailable") gaps.push([`Фінансові дані за ${dos.financials.years[0].year}–${dos.financials.years.at(-1).year} не отримано`, "finance"]);
  if (dos.smkor?.state !== "checked") gaps.push(["СМКОР не перевірено — запропонуйте бізнес-партнерство (згода контрагента)", "overview"]);
  const notReady = [["санкції", ck.sanctions], ["боржники", ck.debtors], ["виконавчі провадження", ck.enforcement], ["суди", ck.courts], ["Prozorro", dos.prozorro?.state === "checked" ? { state: "checked" } : null]].filter(([, b]) => !b || b.state === "not_connected").map(([n]) => n);
  if (notReady.length) gaps.push([`Не перевірено (джерело ще не завантажено): ${notReady.join(", ")}`, "checks"]);
  const sum = (dir, k) => inv.filter((x) => x.direction === dir).reduce((a, x) => a + Number(x[k] || 0), 0);
  const last = inv[0];
  const link = (t, label, extra) => <button type="button" className="link-btn" onClick={() => go(t, extra)}>{label}</button>;

  return (
    <>
      <TrustBlock companyId={companyId} company={company} tin={tin} name={name} onInfo={() => { if (dos.smkor?.state !== "checked") refresh(); }} />
      {dos.score && <ScoreCard score={dos.score} />}
      {(attention.length > 0 || gaps.length > 0) && (
        <section className="card" aria-labelledby="ov-att">
          <h3 id="ov-att" className="block-title">Важливо</h3>
          <ul className="att-list">
            {attention.map(([tone, t, tab], i) => (
              <li key={`a${i}`} className={`att att-${tone}`}><span className="att-mark" aria-hidden="true" />
                <div className="att-body"><p className="att-title"><span className="sr-only">{tone === "bad" ? "Ризик: " : "Зауваження: "}</span>{t}</p></div>{link(tab, "Деталі")}</li>
            ))}
            {gaps.map(([t, tab], i) => (
              <li key={`g${i}`} className="att att-info"><span className="att-mark" aria-hidden="true" />
                <div className="att-body"><p className="att-title"><span className="sr-only">Прогалина: </span>{t}</p></div>{link(tab, "Деталі")}</li>
            ))}
          </ul>
          <p className="notice">Прогалини — це неперевірені джерела, а не встановлені ризики.</p>
        </section>
      )}

      <section className="card" aria-labelledby="ov-deals">
        <div className="row"><h3 id="ov-deals" className="block-title">Наша співпраця</h3>{inv.length > 0 && link("deals", "Усі документи →")}</div>
        {!d ? <p className="muted">Завантаження…</p> : !inv.length ? <p className="muted">ПН/РК з цим контрагентом у ЄРПН компанії немає.</p> : (
          <>
            <p className="muted small">За ПН/РК, завантаженими з ЄРПН; це обороти, а не оплата чи заборгованість</p>
            <div className="stats">
              <div><span className="muted">Продано, з ПДВ</span>{link("deals", uah(sum("out", "total")), { filter: "out" })}<span className="muted small">ПДВ {money(sum("out", "vat_sum"))} · {inv.filter((x) => x.direction === "out").length} док.</span></div>
              <div><span className="muted">Придбано, з ПДВ</span>{link("deals", uah(sum("in", "total")), { filter: "in" })}<span className="muted small">ПДВ {money(sum("in", "vat_sum"))} · {inv.filter((x) => x.direction === "in").length} док.</span></div>
              <div><span className="muted">Без операції в СЕА</span>{link("deals", String(inv.filter((x) => !x.in_sea).length), { filter: "nosea" })}</div>
              <div><span className="muted">Зупинені ПН/РК</span><b className={d.stopped?.some((s) => s.status === 1) ? "error" : ""}>{d.stopped ? d.stopped.filter((s) => s.status === 1).length : "—"}</b></div>
            </div>
            {last && <p className="small">Остання: {last.direction === "in" ? "отримана" : "видана"} {last.doc_kind || "ПН"} № {last.nmr} від {day(last.crtdate)}, {uah(last.total)} · {last.status_name}</p>}
          </>
        )}
      </section>

      <div className="dz-grid">
        <section className="card" aria-labelledby="ov-co">
          <div className="row"><h3 id="ov-co" className="block-title">Компанія</h3>{link("company", "Усі реквізити →")}</div>
          <dl className="kv">
            <dt>Дата реєстрації</dt><dd>{edr ? isoUa(edr.registration_date) : "—"}</dd>
            <dt>Статутний капітал</dt><dd>{edr?.authorized_capital != null ? uah(edr.authorized_capital) : "—"}</dd>
            <dt>Керівник</dt><dd>{director ? director.split(";")[0] : "—"}</dd>
            <dt>Адреса</dt><dd>{dpsReg?.address || <span className="muted">{dos.tax?.state === "checked" ? "ДПС не повернула" : "з ДПС — після перевірки"}</span>}</dd>
            <dt>Власники</dt><dd>{edr ? `${edr.founders.length} засновн. · ${edr.beneficiaries.length} бенефіц.` : "—"}</dd>
            <dt>Рахунок</dt><dd>{mainAcc ? <><span className="mono">{ibanGroups(mainAcc.iban)}</span>{mainAcc.bank ? <span className="muted small"> · {mainAcc.bank}</span> : null}</>
              : <button type="button" className="link-btn" onClick={() => go("accounts")}>{accounts.length ? "лише валютні — див. «Рахунки»" : "не вказано — додати"}</button>}</dd>
          </dl>
        </section>
        <section className="card" aria-labelledby="ov-tax">
          <div className="row"><h3 id="ov-tax" className="block-title">Податковий статус</h3>{link("taxes", "Усі перевірки →")}</div>
          <dl className="kv">
            <dt>ПДВ</dt><dd>{vs.text}</dd>
            <dt>Єдиний податок</dt><dd>{verdict?.singleTax ? `${verdict.singleTax.active ? "" : "був, "}${verdict.singleTax.group} група` : verdict?.registers?.edpod === "empty" ? "записів немає" : "не перевірено"}</dd>
            <dt>Система оподаткування</dt><dd>{verdict?.singleTax?.active ? "спрощена" : <span className="muted">не встановлена</span>}</dd>
            <dt>Керівник-боржник</dt><dd>{verdict?.registers?.debtManager === "found" ? <span className="error">так</span> : verdict?.registers?.debtManager === "empty" ? "ні" : "не перевірено"}</dd>
            <dt>СМКОР</dt><dd>не перевірено (потрібна згода)</dd>
          </dl>
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- Компанія
function People({ title, list, limit = 5 }) {
  const [open, setOpen] = useState(false);
  const shown = open ? list : list.slice(0, limit);
  return (
    <>
      <h4 className="kv-sub">{title}{list.length ? ` (${list.length})` : ""}</h4>
      {list.length ? (
        <>
          <ul className="plain-list">{shown.map((x, i) => <li key={i}>{x}</li>)}</ul>
          {list.length > limit && <button type="button" className="link-btn no-print" onClick={() => setOpen(!open)}>{open ? "Згорнути" : `Показати всіх (${list.length})`}</button>}
        </>
      ) : <p className="muted small">У відкритих даних не вказано</p>}
    </>
  );
}
function CompanyTab({ reg, edr, dpsReg, dos }) {
  return (
    <section className="card" aria-label="Реквізити компанії">
      {(!reg || reg.state === "not_connected") ? <p className="muted">ЄДР ще не завантажено — див. «Джерела».</p>
        : reg.state === "unsupported" ? <p className="muted">{reg.reason}.</p>
        : reg.state === "not_found" ? <p className="muted">У відкритому ЄДР на {isoUa(reg.asOf)} юрособи з цим кодом немає.</p>
        : (
          <>
            <p className="muted small">ЄДР: відкриті дані НАІС на {isoUa(edr.asOf)} — не офіційний витяг. Адреса — з реєстру ДПС.</p>
            <dl className="kv">
              <dt>Повна назва</dt><dd>{edr.name || "—"}</dd>
              {edr.short_name && <><dt>Скорочена назва</dt><dd>{edr.short_name}</dd></>}
              {edr.opf && <><dt>Організаційно-правова форма</dt><dd>{edr.opf}</dd></>}
              <dt>Стан</dt><dd className={edr.stan !== "зареєстровано" ? "error" : ""}>{edr.stan || "—"}</dd>
              <dt>Дата реєстрації</dt><dd>{isoUa(edr.registration_date)}{edr.registration ? <span className="muted small"> · {edr.registration}</span> : null}</dd>
              <dt>Адреса</dt><dd>{dpsReg?.address ? <>{dpsReg.address} <span className="muted small">· ДПС, {dateTime(dos.tax.checkedAt)}</span></> : <span className="muted">ДПС {dos.tax?.state === "checked" ? "не повернула адресу" : "ще не перевіряли"}; у відкритому ЄДР адреси немає</span>}</dd>
              <dt>Статутний капітал</dt><dd>{edr.authorized_capital != null ? uah(edr.authorized_capital) : "—"}</dd>
              <dt>Органи управління</dt><dd>{edr.management || "—"}</dd>
              <dt>КВЕД</dt><dd>{dos.financials?.kved ? <>{dos.financials.kved.code}{dos.financials.kved.name ? ` ${dos.financials.kved.name}` : ""} <span className="muted small">· з фінзвітності за {dos.financials.kved.year}</span></> : <span className="muted">у відкритому ЄДР і реєстрах ДПС немає; у фінзвітності Держстату не знайдено</span>}</dd>
              {Object.keys(edr.termination || {}).length > 0 && <><dt>Припинення</dt><dd className="error">{Object.values(edr.termination).join(" · ")}</dd></>}
              {edr.branches > 0 && <><dt>Відокремлені підрозділи</dt><dd>{edr.branches}</dd></>}
            </dl>
            <People title="Керівник і підписанти" list={edr.signers || []} />
            <People title="Засновники" list={edr.founders || []} />
            <People title="Кінцеві бенефіціари" list={edr.beneficiaries || []} />
            {edr.predecessors?.length > 0 && <People title="Правопопередники" list={edr.predecessors} />}
            {edr.assignees?.length > 0 && <People title="Правонаступники" list={edr.assignees} />}
          </>
        )}
    </section>
  );
}

// ---------------------------------------------------------------- Податки
function TaxesTab({ companyId, company, name, tin, dos, verdict, edr, dpsReg, check, checking }) {
  const vat = dos.vat;
  return (
    <>
      <section className="card" aria-labelledby="tx-vat">
        <h3 id="tx-vat" className="block-title">ПДВ</h3>
        {!vat || vat.state === "not_checked" ? <p className="muted">Не перевірено — натисніть «Оновити ДПС».</p> : (
          <>
            <p>{vatShort(vat).text}{vat.vatNumber ? <> · ІПН <b className="mono">{vat.vatNumber}</b></> : null}
              {vat.state === "annulled" && <span className="muted"> — історичний номер, не для нових ПН</span>}</p>
            {vat.history?.length > 0 && (
              <div className="table-wrap"><table className="table-static">
                <thead><tr><th>ІПН</th><th>Реєстрація</th><th>Анулювання</th><th>Підстава</th></tr></thead>
                <tbody>{vat.history.map((h, i) => <tr key={i}><td className="mono">{h.vatNumber || "—"}</td><td>{isoUa(h.registrationDate)}</td><td>{h.annulmentDate ? isoUa(h.annulmentDate) : "—"}</td><td>{h.annulmentReason || "—"}</td></tr>)}</tbody>
              </table></div>
            )}
          </>
        )}
      </section>

      <section className="card check-card" aria-labelledby="tx-dps" aria-busy={checking}>
        <div className="row">
          <h3 id="tx-dps" className="block-title">Реєстри ДПС</h3>
          <button type="button" className="btn btn-sm no-print" onClick={check} disabled={checking}>{checking ? "Перевіряємо…" : verdict ? "Перевірити ще раз" : "Перевірити"}</button>
        </div>
        {!verdict ? <p className="muted">Ще не перевірявся. Перевірка йде під вашою сесією ДПС і зберігається для компанії.</p> : (
          <>
            <p className="muted small">ДПС перевірено {dateTime(dos.tax.checkedAt)}{dos.tax.stale && <> · <span className="warn-text">відомості старші за 30 днів</span></>}</p>
            {dpsReg && (
              <dl className="kv">
                <dt>На обліку</dt><dd>з {isoUa(dpsReg.registeredAt)}{dpsReg.closedAt ? <span className="error">, знято {isoUa(dpsReg.closedAt)}</span> : ""}</dd>
                <dt>Орган обліку</dt><dd>{dpsReg.authority || "—"}{dpsReg.authorityCode ? <span className="muted small"> · код {dpsReg.authorityCode}</span> : null}</dd>
                <dt>Адреса платника</dt><dd>{dpsReg.address || <span className="muted">не повернула</span>}</dd>
              </dl>
            )}
            <ul className="att-list check-list">
              {verdict.items.filter((it) => it.key !== "registration" || !dpsReg).map((it) => (
                <li key={it.key} className={`att att-${it.tone}`}><span className="att-mark" aria-hidden="true" />
                  <div className="att-body"><p className="att-title"><span className="sr-only">{it.tone === "bad" ? "Ризик: " : it.tone === "warn" ? "Не перевірено: " : ""}</span>{it.title}</p>{it.detail && it.key !== "pdv" && <p className="att-detail">{it.detail}</p>}</div>
                </li>
              ))}
            </ul>
            {verdict.unchecked?.length > 0 && <p className="notice">Не дали відповіді: {verdict.unchecked.join(", ")}. Відсутність записів у них не підтверджена.</p>}
            <p className="notice">Коди станів, значення яких ДПС не публікує, не тлумачимо. Відсутність запису про єдиний податок не означає загальної системи.</p>
          </>
        )}
      </section>

      {edr?.exchange?.some((x) => x.tax_payer_type) && (
        <section className="card" aria-labelledby="tx-nais">
          <h3 id="tx-nais" className="block-title">Облік за даними ЄДР</h3>
          <p className="muted small">Відомості, передані до ЄДР податковими органами; НАІС на {isoUa(edr.asOf)}</p>
          <ul className="plain-list">{edr.exchange.filter((x) => x.tax_payer_type).map((x, i) => (
            <li key={i}>{x.tax_payer_type}: з {x.start_date || "—"}{x.start_num ? `, № ${x.start_num}` : ""}{x.end_date ? `, до ${x.end_date}` : ""}</li>
          ))}</ul>
        </section>
      )}

    </>
  );
}

// ---------------------------------------------------------------- Фінанси
const FIN_ROWS = [["revenue", "Чистий дохід"], ["netProfit", "Чистий прибуток (збиток)"], ["margin", "Рентабельність"], ["assets", "Активи"],
  ["liabilities", "Зобов'язання"], ["equity", "Власний капітал"], ["employees", "Працівники"]];
const KIND = { full: "повна", small: "мала", micro: "мікро" };
const SIGNAL_TONE = { red: "bad", yellow: "warn", grey: "idle" };
function finCell(y, key) {
  if (y.state !== "available") return <span className="muted small">{YEAR_STATE[y.state] || y.state}</span>;
  const v = y.values;
  if (key === "margin") return v.revenue > 0 && v.netProfit != null ? `${(v.netProfit / v.revenue * 100).toLocaleString("uk-UA", { maximumFractionDigits: 2 })}%` : "—";
  if (key === "employees") return v.employees != null ? v.employees : "—";
  if (v[key] == null) return "—";
  return <span className={key === "netProfit" && v[key] < 0 ? "error" : ""}>{money(v[key])}</span>;
}
function FinanceTab({ dos }) {
  const fin = dos.financials, src = dos.sources?.financials;
  const avail = fin.years.filter((y) => y.state === "available");
  const range = `${fin.years[0].year}–${fin.years.at(-1).year}`;
  return (
    <>
      {fin.signals?.length > 0 && (
        <section className="card" aria-labelledby="fin-sig">
          <h3 id="fin-sig" className="block-title">Сигнали для перевірки</h3>
          <ul className="plain-list">{fin.signals.map((sg, i) => (
            <li key={i}><span className={`badge badge-${SIGNAL_TONE[sg.severity] || "idle"}`}>{sg.year}</span> {sg.title}
              <span className="muted small"> · {sg.formula}{sg.threshold && sg.threshold !== "—" ? `, поріг ${sg.threshold}` : ""}</span></li>
          ))}</ul>
          <p className="muted small">Пороги — налаштування eCabinet ({fin.rules}), не норми закону й не офіційний статус ДПС.</p>
        </section>
      )}
      <section className="card" aria-label="Фінансова звітність">
        <p><b>{avail.length ? `Звітність за ${avail.length} з ${fin.years.length} років` : fin.state === "source_unavailable" ? `Фінансові дані за ${range} не отримано` : `За ${range} звітів у наборі Держстату не знайдено`}</b>
          {fin.kved && <span className="muted small"> · КВЕД {fin.kved.code}{fin.kved.name ? ` ${fin.kved.name}` : ""}</span>}</p>
        <div className="table-wrap">
          <table className="table-static fin-table">
            <thead><tr><th>Показник, грн</th>{fin.years.map((y) => <th key={y.year} className="num">{y.year}{y.comparative ? "*" : ""}</th>)}</tr></thead>
            <tbody>{FIN_ROWS.map(([key, label]) => (
              <tr key={key}><td>{label}</td>{fin.years.map((y) => <td key={y.year} className="num">{finCell(y, key)}</td>)}</tr>
            ))}
              <tr><td className="muted small">Форма</td>{fin.years.map((y) => <td key={y.year} className="num muted small">{y.state === "available" ? KIND[y.kind] || y.kind : ""}</td>)}</tr>
            </tbody>
          </table>
        </div>
        {fin.years.some((y) => y.comparative) && <p className="muted small">* — з порівняльних колонок звіту за наступний рік (власного звіту за цей рік у наборі немає).</p>}
        <p className="notice">{src?.owner}: {src?.note}{src?.asOf ? ` · дані на ${isoUa(src.asOf)}` : ""}. «Не знайдено» — у наборі немає звіту (зокрема ФОП і платники ЄП звітність до Держстату зазвичай не подають), а не «звітності немає».</p>
      </section>
    </>
  );
}

// ---------------------------------------------------------------- Перевірки
const ROLE = { debtor: "боржник", creditor: "стягувач" };
const SANCTION = { active: "діючі санкції", expired: "строк санкцій сплив", excluded: "санкції знято", updated: "запис оновлено" };
function SrcLine({ src }) {
  if (!src) return null;
  return <p className="muted small">{src.state === "ready" ? `Дані на ${isoUa(src.asOf)}` : src.note || "Джерело ще не завантажено"}</p>;
}
function NotReady({ block, what }) {
  if (block.state === "unsupported") return <p className="muted">{what} перевіряємо лише для юросіб за кодом ЄДРПОУ.</p>;
  return <p className="muted">Не перевірено: джерело ще не завантажено. Це не означає, що записів немає.</p>;
}
function ChecksTab({ dos, verdict }) {
  const ch = dos.checks || {};
  const deb = ch.debtors, enf = ch.enforcement, courts = ch.courts, sanc = ch.sanctions;
  const statSum = (role, active) => (enf?.stats || []).filter((x) => x.role === role).reduce((a, x) => a + x.n, 0);
  const [openCase, setOpenCase] = useState(null);
  return (
    <>
      <section className="card" aria-labelledby="ck-sanc">
        <h3 id="ck-sanc" className="block-title">Санкції РНБО</h3>
        <SrcLine src={sanc?.source} />
        {!sanc || sanc.state !== "checked" ? <p className="muted">{sanc?.state === "unsupported" ? "Перевіряємо лише юрособи за кодом." : "Не перевірено: файл реєстру санкцій ще не завантажено. Це не означає, що санкцій немає."}</p>
          : !sanc.items.length ? <p><span className="badge badge-ok">Не знайдено</span> <span className="muted small">за кодом {dos.identity.value} серед юросіб реєстру</span></p>
          : <ul className="plain-list">{sanc.items.map((x) => (
            <li key={x.sid}><span className={`badge badge-${x.status === "active" ? "bad" : "warn"}`}>{SANCTION[x.status] || x.status}</span> {x.name}
              <span className="muted small"> · запис {x.sid}{x.decree ? ` · указ ${x.decree}${x.decree_date ? ` від ${isoUa(x.decree_date)}` : ""}` : ""}{x.term ? ` · строк: ${x.term}` : ""}{x.end_date ? ` (до ${isoUa(x.end_date)})` : ""}</span>
              {x.restrictions && <details><summary className="small">Обмеження</summary><p className="small">{x.restrictions}</p></details>}</li>
          ))}</ul>}
      </section>

      <section className="card" aria-labelledby="ck-erb">
        <h3 id="ck-erb" className="block-title">Єдиний реєстр боржників</h3>
        <SrcLine src={deb?.source} />
        {!deb || deb.state !== "checked" ? <NotReady block={deb || {}} what="Боржників" />
          : !deb.items.length ? <p><span className="badge badge-ok">Записів немає</span></p> : (
            <>
              <p><span className="badge badge-bad">Є в реєстрі боржників</span> {deb.items.length} запис(ів)</p>
              <div className="table-wrap"><table className="table-static">
                <thead><tr><th>№ ВП</th><th>Категорія стягнення</th><th>Хто видав документ</th><th>Орган виконання</th></tr></thead>
                <tbody>{deb.items.map((x, i) => <tr key={i}><td className="mono">{x.vp_ordernum || "—"}</td><td>{x.category || "—"}</td><td>{x.publisher || "—"}</td><td>{x.org_name || "—"}</td></tr>)}</tbody>
              </table></div>
            </>
          )}
      </section>

      <section className="card" aria-labelledby="ck-asvp">
        <h3 id="ck-asvp" className="block-title">Виконавчі провадження</h3>
        <SrcLine src={enf?.source} />
        {!enf || enf.state !== "checked" ? <NotReady block={enf || {}} what="Провадження" />
          : !enf.stats.length ? <p><span className="badge badge-ok">Проваджень немає</span></p> : (
            <>
              <dl className="kv">
                <dt>Як боржник</dt><dd>{statSum("debtor") ? enf.stats.filter((x) => x.role === "debtor").map((x) => `${x.state}: ${x.n}`).join(" · ") : "немає"}</dd>
                <dt>Як стягувач</dt><dd>{statSum("creditor") ? enf.stats.filter((x) => x.role === "creditor").map((x) => `${x.state}: ${x.n}`).join(" · ") : "немає"}</dd>
              </dl>
              {enf.active.length > 0 && (
                <div className="table-wrap"><table className="table-static">
                  <thead><tr><th>Роль</th><th>№ ВП</th><th>Відкрито</th><th>Стан</th><th>Інша сторона</th><th>Орган виконання</th></tr></thead>
                  <tbody>{enf.active.map((x, i) => <tr key={i}><td className={x.role === "debtor" ? "error" : ""}>{ROLE[x.role]}</td><td className="mono">{x.vp_ordernum}</td><td>{isoUa(x.begin_date)}</td><td>{x.state}</td><td>{x.other || "—"}{x.other_code ? <span className="muted small"> · {x.other_code}</span> : null}</td><td>{x.org_name || "—"}</td></tr>)}</tbody>
                </table></div>
              )}
              <p className="notice">Незавершені провадження — у таблиці, завершені — кількістю. Суми стягнення у відкритих даних немає.</p>
            </>
          )}
      </section>

      <Prozorro pz={dos.prozorro} src={dos.sources?.prozorro} />

      <section className="card" aria-labelledby="ck-courts">
        <h3 id="ck-courts" className="block-title">Судові справи{courts?.cases?.length ? ` (${courts.cases.length})` : ""}</h3>
        <SrcLine src={courts?.source} />
        {!courts || courts.state === "not_connected" ? <NotReady block={{}} what="Суди" />
          : courts.state === "no_name" ? <p className="muted">Немає назви з ЄДР чи ДПС — шукати справи нема за чим.</p>
          : !courts.cases.length ? <p><span className="badge badge-ok">Справ не знайдено</span> <span className="muted small">серед призначених до розгляду за назвою «{courts.keys.join(" / ")}»</span></p> : (
            <>
              <p className="notice">Збіг за назвою «{courts.keys.join(" / ")}»: у відкритих даних судів сторони вказані без кодів, тож тезки можливі. Список охоплює справи, призначені до розгляду; рішення — з ЄДРСР за номером справи{courts.decisionsSource?.state === "ready" ? "" : " (рішення ще не завантажено)"}.</p>
              <ul className="case-list">
                {courts.cases.map((x) => (
                  <li key={x.caseNum}>
                    <button type="button" className="case-head" aria-expanded={openCase === x.caseNum} onClick={() => setOpenCase(openCase === x.caseNum ? null : x.caseNum)}>
                      <b className="mono">{x.caseNum}</b> · {x.court} · <span className={x.roles.some((r) => /відповідач|боржник/i.test(r)) ? "warn-text" : ""}>{x.roles.join(", ")}</span>
                      <span className="muted small"> · засідання {x.hearings[0]}{x.decisions.length ? ` · рішень ${x.decisions.length}` : ""}</span>
                    </button>
                    {(openCase === x.caseNum) && (
                      <div className="case-body">
                        {x.description && <p>{x.description}</p>}
                        {x.involved && <p className="muted small">{x.involved}</p>}
                        {x.decisions.length > 0 && <ul className="plain-list">{x.decisions.map((d) => (
                          <li key={d.doc_id}>{isoUa(d.adjudication_date)} · {d.judgment_form} · {d.justice_kind} · {d.court_name}{d.judge ? ` · ${d.judge}` : ""} — {d.status === 1 ? <a href={d.url} target="_blank" rel="noreferrer noopener">текст у ЄДРСР</a> : <span className="muted">текст ще не оприлюднено</span>}</li>
                        ))}</ul>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
      </section>

      <section className="card" aria-labelledby="ck-dm">
        <h3 id="ck-dm" className="block-title">Реєстр керівників платників-боржників (ДПС)</h3>
        <p>{verdict?.registers?.debtManager === "found" ? <span className="badge badge-bad">є записи</span> : verdict?.registers?.debtManager === "empty" ? <span className="badge badge-ok">записів немає</span> : <span className="badge badge-idle">не перевірено — «Оновити ДПС»</span>}</p>
      </section>
    </>
  );
}

// Участь у публічних закупівлях Prozorro (за кодом ЄДРПОУ в пропозиції)
const PZ_STATUS = { complete: "завершено", active: "триває", unsuccessful: "не відбулась", cancelled: "скасовано" };
function Prozorro({ pz, src }) {
  return (
    <section className="card" aria-labelledby="ck-pz">
      <h3 id="ck-pz" className="block-title">Публічні закупівлі Prozorro</h3>
      {!pz || pz.state === "not_connected" ? <p className="muted">Не перевірено: джерело ще не підключено.</p>
        : pz.state === "unavailable" ? <p className="muted">{pz.reason}. Спробуйте пізніше.</p>
        : pz.state === "unsupported" ? <p className="muted">Учасників Prozorro шукаємо за кодом ЄДРПОУ юрособи.</p>
        : (
          <>
            <p className="muted small">Дані на {src?.asOf ? isoUa(src.asOf) : "—"} · закупівлі з 15.09.2024</p>
            {!pz.participated && !pz.asBuyer ? <p>У закупівлях Prozorro не брала участі.</p> : (
              <div className="stats">
                <div><span className="muted">Участь (пропозицій)</span><b>{pz.participated}</b></div>
                <div><span className="muted">Перемоги</span><b>{pz.won}</b><span className="muted small">{uah(pz.wonAmount)}</span></div>
                <div><span className="muted">Договори</span><b>{pz.contracts}</b><span className="muted small">{uah(pz.contractsAmount)} · виконано {pz.contractsDone}</span></div>
                <div><span className="muted">Відхилено (кваліфікація)</span><b>{pz.rejected}</b></div>
                {pz.asBuyer > 0 && <div><span className="muted">Як замовник</span><b>{pz.asBuyer}</b><span className="muted small">{uah(pz.asBuyerAmount)}</span></div>}
              </div>
            )}
            {pz.buyers?.length > 0 && <><h4 className="kv-sub">Основні замовники</h4><ul className="plain-list">{pz.buyers.map((b) => <li key={b.buyer_edrpou || b.buyer_name}>{b.buyer_name} <span className="muted small">· {b.buyer_edrpou} · {b.n} перемог(и), {uah(b.amount)}</span></li>)}</ul></>}
            {pz.recent?.length > 0 && (
              <div className="table-wrap"><table className="table-static">
                <thead><tr><th>Дата</th><th>Закупівля</th><th>Замовник</th><th className="num">Очікувана</th><th className="num">Пропозиція</th><th>Результат</th></tr></thead>
                <tbody>{pz.recent.map((t) => (
                  <tr key={t.tender_id}><td>{isoUa(t.date_created)}</td><td className="wrap-cell"><a href={t.url} target="_blank" rel="noreferrer noopener">{t.tender_id}</a><br /><span className="small">{t.title}</span></td>
                    <td className="wrap-cell small">{t.buyer_name}</td><td className="num">{t.value != null ? money(t.value) : "—"}</td><td className="num">{t.bid != null ? money(t.bid) : "—"}</td>
                    <td>{t.won ? <span className="badge badge-ok">перемога</span> : t.rejected ? <span className="badge badge-warn">відхилено</span> : <span className="muted">{PZ_STATUS[t.status] || t.status}</span>}</td></tr>
                ))}</tbody>
              </table></div>
            )}
          </>
        )}
    </section>
  );
}

// ---------------------------------------------------------------- Співпраця
const FILTERS = [["all", "Усі"], ["out", "Видані"], ["in", "Отримані"], ["nosea", "Без операції в СЕА"]];
function DealsTab({ companyId, d, r, inv, dealFilter, setDealFilter }) {
  const [open, setOpen] = useState(null), [page, setPage] = useState(0);
  const list = useMemo(() => inv.filter((x) => dealFilter === "all" || (dealFilter === "nosea" ? !x.in_sea : x.direction === dealFilter)), [inv, dealFilter]);
  useEffect(() => { setPage(0); setOpen(null); }, [dealFilter]);
  if (r.pending && !d) return <p className="muted" role="status">Завантаження…</p>;
  if (r.error && !d) return <p className="error">Не вдалося завантажити операції: {r.error.message}</p>;
  if (!inv.length) return <section className="card"><p className="muted">ПН/РК з цим контрагентом у ЄРПН компанії немає.</p></section>;
  const SIZE = 10, pages = Math.ceil(list.length / SIZE), rows = list.slice(page * SIZE, (page + 1) * SIZE);
  // Документ відкривається лише після вибору
  const selected = list.find((x) => x.code === open) || null;
  const sum = (k) => list.reduce((a, x) => a + Number(x[k] || 0), 0);
  return (
    <section className="card" aria-label="ПН/РК з контрагентом">
      <div className="row toolbar">
        <div className="tabs" role="group" aria-label="Фільтр документів">
          {FILTERS.map(([k, t]) => <button key={k} type="button" className={dealFilter === k ? "tab active" : "tab"} aria-pressed={dealFilter === k} onClick={() => setDealFilter(k)}>{t}</button>)}
        </div>
        <span className="muted small">{list.length} док. · {uah(sum("total"))} з ПДВ · ПДВ {money(sum("vat_sum"))}</span>
      </div>
      {!list.length ? <p className="muted">За цим фільтром документів немає.</p> : (
        <div className="table-wrap">
          <table className="table-static">
            <thead><tr><th>Дата</th><th>№</th><th>Напрям</th><th className="num">Сума з ПДВ</th><th className="num">ПДВ</th><th>Статус</th><th>СЕА</th></tr></thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.code} tabIndex={0} aria-selected={selected?.code === x.code} className={selected?.code === x.code ? "clickable selected" : "clickable"}
                  onClick={() => setOpen(selected?.code === x.code ? null : x.code)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(selected?.code === x.code ? null : x.code); } }}>
                  <td>{day(x.crtdate)}</td><td>{x.nmr}</td><td>{x.direction === "in" ? "Отримана" : "Видана"} {x.doc_kind || "ПН"}</td>
                  <td className="num">{money(x.total)}</td><td className="num">{money(x.vat_sum)}</td>
                  <td>{x.status_name}</td><td>{x.in_sea ? "є" : <span className="muted">немає</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && <Pager page={page} pages={pages} total={list.length} go={(n) => { setPage(n); setOpen(null); }} />}
      {selected ? <InvoicePanel key={selected.code} companyId={companyId} direction={selected.direction} inv={{ ...selected, buyer: selected.name, seller: selected.name }} />
        : <p className="muted small no-print">Оберіть документ, щоб переглянути PDF, квитанції й підписаний оригінал (p7s).</p>}
    </section>
  );
}

// ---------------------------------------------------------------- Джерела
function SourcesTab({ dos, verdict }) {
  const src = dos.sources || {};
  return (
    <section className="card" aria-label="Джерела й актуальність">
      <p className="muted small">Кожне джерело має власну дату. Покриття — не оцінка надійності контрагента.</p>
      <ul className="cov-list">
        {Object.entries(src).map(([k, s]) => {
          const [label, tone] = k === "dps" ? [dos.tax?.state === "checked" ? `перевірено ${dateTime(dos.tax.checkedAt)}` : "ще не перевіряли", dos.tax?.state === "checked" ? "ok" : "idle"] : SRC_STATE[s.state] || [s.state, "idle"];
          return (
            <li key={k}>
              <span><b>{s.title}</b><span className="muted small"> · {s.owner}</span></span>
              <span className={`badge badge-${tone}`}>{label}</span>
              <span className="muted small cov-note">{k === "dps" && verdict ? `Відповіли: ${verdict.checked.join(", ") || "—"}${verdict.unchecked.length ? `. Не відповіли: ${verdict.unchecked.join(", ")}` : ""}` : s.note}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
